import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Mail, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import Button from '../../components/UI/Button';
import {
  AuthCard,
  AuthCardHeader,
  AuthField,
  AuthInputWrapper,
  authInputWithIconClass,
  AuthOTPInput,
  AuthLaunchTransition,
  AuthPageFrame,
} from '../../components/Auth';


const RESEND_COOLDOWN_SEC = 60;
const RESEND_VERIFICATION_GENERIC =
  'If an account exists for that email and is not yet verified, we sent a new verification code.';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmailAndLogin, isAuthenticated, isLoading: authBootLoading, clearError, getPostAuthDestination } =
    useAuth();

  const emailFromQuery = searchParams.get('email')?.trim() ?? '';
  const nextAfterVerify = searchParams.get('next')?.trim() ?? '';
  const emailFromState = (location.state as { email?: string } | null)?.email?.trim() ?? '';
  const [email, setEmail] = useState(emailFromQuery || emailFromState);
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [pendingDestination, setPendingDestination] = useState<string | null>(null);

  useEffect(() => {
    if (emailFromQuery && emailFromQuery !== email) {
      setEmail(emailFromQuery);
    }
  }, [emailFromQuery, email]);

  useEffect(() => {
    if (!authBootLoading && isAuthenticated) {
      navigate(getPostAuthDestination(nextAfterVerify), { replace: true });
    }
  }, [authBootLoading, getPostAuthDestination, isAuthenticated, navigate, nextAfterVerify]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = window.setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [resendCooldown]);

  const maskedEmail = email.includes('@')
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => `${a}${'*'.repeat(Math.min(b.length, 6))}${c}`)
    : email;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const code = otp.replace(/\D/g, '');
    setFormError(null);
    setResendMessage(null);

    if (!trimmedEmail) {
      setFormError('Enter your email address.');
      return;
    }
    if (code.length !== 6) {
      setFormError('Enter the 6-digit code from your email.');
      return;
    }

    try {
      setSubmitting(true);
      clearError();
      setVerified(true);
      const destination = await verifyEmailAndLogin(trimmedEmail, code, nextAfterVerify);
      setPendingDestination(destination);
      setLaunching(true);
    } catch (_err) {
      setVerified(false);
      setFormError(_err instanceof Error ? _err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = useCallback(async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError('Enter your email address first.');
      return;
    }
    if (resendCooldown > 0 || resending) return;

    try {
      setResending(true);
      setFormError(null);
      await authService.resendVerification(trimmedEmail);
      setResendMessage(RESEND_VERIFICATION_GENERIC);
      setResendCooldown(RESEND_COOLDOWN_SEC);
    } catch (_err) {
      setFormError(_err instanceof Error ? _err.message : 'Could not send code');
    } finally {
      setResending(false);
    }
  }, [email, resendCooldown, resending]);

  return (
    <>
      <AuthLaunchTransition
        active={launching}
        onComplete={() => {
          if (pendingDestination) navigate(pendingDestination, { replace: true });
        }}
      />
      <AuthPageFrame>
      <AuthCard>
        <AuthCardHeader
          title="Verify your email"
          subtitle="Enter the 6-digit code we sent to activate your account"
        />

        {email ? (
          <motion.div
            className="mb-6 flex items-center gap-3 rounded-xl border border-gray-200/60 bg-gray-50 px-4 py-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
              <Mail className="h-4 w-4 text-blue-600" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500">Code sent to</p>
              <p className="truncate text-sm font-medium text-gray-900">{maskedEmail}</p>
            </div>
          </motion.div>
        ) : null}

        <AnimatePresence mode="wait">
          {verified && submitting ? (
            <motion.div
              key="verifying"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3"
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
              <p className="text-sm text-emerald-800">
                Verification successful. Launching your dashboard…
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <AuthField label="Email address" htmlFor="verify-email">
            <AuthInputWrapper>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Mail className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
              </div>
              <input
                id="verify-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className={authInputWithIconClass()}
                required
                disabled={submitting || authBootLoading}
              />
            </AuthInputWrapper>
          </AuthField>

          <AuthField label="Verification code" htmlFor="verify-otp">
            <AuthOTPInput
              value={otp}
              onChange={setOtp}
              disabled={submitting || authBootLoading || verified}
            />
          </AuthField>

          {formError ? (
            <motion.div
              role="alert"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
              <p className="text-sm text-red-500">{formError}</p>
            </motion.div>
          ) : null}

          {resendMessage ? (
            <motion.div
              role="status"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              <p className="text-sm text-emerald-800">{resendMessage}</p>
            </motion.div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={submitting || authBootLoading}
            disabled={verified}
            className="w-full rounded-xl"
          >
            Verify and continue
            {!submitting && !authBootLoading ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden /> : null}
          </Button>
        </form>

        <motion.div
          className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || resending || !email.trim()}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : resending
                ? 'Sending…'
                : 'Resend code'}
          </button>
          <Link
            to="/login"
            className="text-center text-sm font-medium text-gray-600 hover:text-gray-900 sm:text-right"
          >
            Back to sign in
          </Link>
        </motion.div>

      </AuthCard>
      </AuthPageFrame>
    </>
  );
};

export default EmailVerification;
